import ModalBase from 'LuxKit/Modal/ModalBase'
import { useCallback, useMemo, useState } from 'react'
import useExperience from 'hooks/Experiences/useExperience'
import { EmptyArray, uniqueBy } from 'lib/array/arrayUtils'
import LoyaltyLoungePassModalBooking from './LoyaltyLoungePassModalBooking'
import type { FlightDateInfo } from './LoyaltyLoungePassModalBooking'
import LoyaltyLoungePassModalSelect from './LoyaltyLoungePassModalSelect'
import type { ExperienceListFilters } from 'actions/ExperienceActions'

type ModalStep = 'airport' | 'tickets'

interface Props {
  /**
   * The default step this modal still starton
   * @default airport
   */
  defaultStep?: ModalStep
  /**
   * Pass a bunch of views to set the default values from the data on the views
   */
  loungePassViews?: Array<App.Checkout.LoyaltyLoungePassItemView>
  availabilityFilters: Array<ExperienceListFilters>
  locationsCountryCodes: Array<string>
  onAddItems: (items: Array<App.Checkout.LoyaltyLoungePassItem>) => void
  afterRemoveItems?: () => void
  checkInDate?: string
  checkOutDate?: string
  flightDates?: Array<FlightDateInfo>
  /** Order this redemption belongs to. Used to share the Infinite free-pass cap with passes already redeemed. */
  bookingOrder?: App.Order
}

function LoyaltyLoungePassModal(props: Props) {
  const {
    defaultStep = 'airport',
    loungePassViews = EmptyArray,
    availabilityFilters,
    locationsCountryCodes,
    onAddItems,
    afterRemoveItems,
    checkInDate,
    checkOutDate,
    flightDates,
    bookingOrder,
  }: Props = props
  // When editing lounges from multiple airports, start at airport selection
  // so the user can choose which lounge to edit
  const hasMultipleExperiences = uniqueBy(loungePassViews, v => v.item.experienceId).length > 1
  const [step, setStep] = useState<ModalStep>(
    defaultStep === 'tickets' && hasMultipleExperiences ? 'airport' : defaultStep,
  )
  const [airportLoungeId, setAirportLoungeId] = useState<string | undefined>(loungePassViews[0]?.item.experienceId)

  const filteredLoungePassViews = useMemo(
    () => loungePassViews.filter(v => v.item.experienceId === airportLoungeId),
    [loungePassViews, airportLoungeId],
  )

  const onAirportSelect = useCallback((experience: App.ExperienceOffer) => {
    if (experience) {
      setStep('tickets')
      setAirportLoungeId(experience.id)
    }
  }, [])

  const onBookingBack = useCallback(() => {
    setStep('airport')
  }, [])

  const [experience] = useExperience(airportLoungeId)

  return <ModalBase size="L" height="max" tabletHeight="auto">
    {step === 'airport' && <LoyaltyLoungePassModalSelect
      onAirportSelect={onAirportSelect}
      locationsCountryCodes={locationsCountryCodes}
      availabilityFilters={availabilityFilters}
    />}
    {step === 'tickets' && experience && <LoyaltyLoungePassModalBooking
      loungePassViews={filteredLoungePassViews}
      experience={experience}
      onBack={onBookingBack}
      onAddItems={onAddItems}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      flightDates={flightDates}
      afterRemoveItems={afterRemoveItems}
      bookingOrder={bookingOrder}
    />}
  </ModalBase>
}

export default LoyaltyLoungePassModal
